import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Option } from 'slim-select/dist/data.d';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import Modal from '../../../components/modal/Modal';
import Button from '../../../../components/form/button/Button';
import { RootState } from '../../../../core/state/state';
import Select from '../../../../components/form/select/Select';
import Radio from '../../../../components/form/radio/Radio';
import { spawnGlobalNotification } from '../../../../core/state/global-notifications/asyncActions';
import { GlobalNotificationTypes } from '../../../../core/enums/GlobalNotificationTypes';
import { ExternalUserStatus } from '../../../../core/enums/ExternalUserStatus';
import { ProjectSubcontractor } from '../../../../core/state/subcontractors/types';
import { changeSubcontractorStatus, getExternalUsersByProjectId } from '../../../../core/state/subcontractors/asyncActions';
import { Color } from '../../../../core/enums/Color';

import './ManageSubcontractorRoleModal.scss';

interface Props {
  isOpen: boolean;
  closeModal: () => void;
}

type FormData = {
  user: string;
  role: ExternalUserStatus;
};

const ManageSubcontractorRoleModal: React.FC<Props> = ({ isOpen, closeModal }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const users = useSelector((state: RootState) =>
    state.subcontractor.projectSubcontractors?.filter((user) => user.statusEnum !== ExternalUserStatus.Unblocked)
  );
  const { projectId } = useParams<{ projectId: string }>();

  const usersToOption = (user: ProjectSubcontractor): Option => ({
    value: user.id.toString(),
    text: `${user.name}`
  });

  const userOptions = useMemo(() => users?.map(usersToOption), [users]);

  const validationSchema = yup
    .object()
    .shape({
      user: yup.string().required(),
      role: yup.string().required()
    })
    .required();

  const {
    handleSubmit,
    control,
    watch,
    formState: { isValid }
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      user: '',
      role: undefined
    },
    resolver: yupResolver(validationSchema)
  });

  const user = watch('user');

  const userAccountOptions = useMemo(
    () => [
      {
        label: t('modal.manageSubcontractorRole.dropdown.option.unlock'),
        value: ExternalUserStatus.Unblocked
      },
      {
        label: t('modal.manageSubcontractorRole.dropdown.option.lock'),
        value: ExternalUserStatus.Locked
      }
    ],
    [t]
  );

  const onSubmit = async (formData: FormData) => {
    await Promise.all([
      dispatch(changeSubcontractorStatus(Number(formData.user), Number(projectId), formData.role)),
      dispatch(
        getExternalUsersByProjectId(Number(projectId), [
          ExternalUserStatus.Active,
          ExternalUserStatus.Locked,
          ExternalUserStatus.Invited,
          ExternalUserStatus.Unblocked
        ])
      )
    ]);
    dispatch(
      spawnGlobalNotification({
        type: GlobalNotificationTypes.Success,
        message: t('modal.manageSubcontractorRole.form.message.success')
      })
    );

    closeModal();
  };

  const title = <h3 className="modal-title">{t('common.manageStatus')}</h3>;
  const buttons = (
    <div className="modal-buttons">
      <Button color={Color.Blue} buttonTypes={['transparent']} onClick={closeModal}>
        <span>{t('common.cancel')}</span>
      </Button>
      <Button disabled={!isValid} form="manage-subcontractor-role" type="submit" buttonTypes={['blue']}>
        {t('common.save')}
      </Button>
    </div>
  );

  return (
    <Modal isOpen={isOpen} closeModal={closeModal} title={title} toolbar={buttons} size="small" height={279}>
      <form id="manage-subcontractor-role" className="manage-subcontractor-role-modal" onSubmit={handleSubmit(onSubmit)}>
        <div className="manage-subcontractor-role-modal__content">
          <Controller
            name="user"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <Select
                id="user"
                label={t('common.companyName')}
                name={name}
                value={value}
                options={userOptions}
                onChange={(option) => {
                  if (Array.isArray(option)) {
                    [option] = option; // eslint-disable-line no-param-reassign
                  }
                  onChange(option.value);
                }}
                addPlaceholderOption
              />
            )}
          />
          <Controller
            name="role"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <Radio
                id="role"
                name={name}
                disabled={!user}
                label={t('common.status')}
                options={userAccountOptions}
                defaultValue={value}
                onChange={onChange}
              />
            )}
          />
        </div>
      </form>
    </Modal>
  );
};

export default ManageSubcontractorRoleModal;
